import { DEFAULT_SOCIAL_MEDIA, normalizeSocialMedia } from './storefrontSocial';
import { DEFAULT_CONTENT_BLOCKS, normalizeContentBlocks } from './storefrontContentBlocks';
import { DEFAULT_DELIVERY_SETTINGS, normalizeDeliverySettings } from './storefrontDelivery';
import { DEFAULT_PAYMENT_SETTINGS, normalizePaymentSettings } from './storefrontPayment';
import { DEFAULT_MENU_SETTINGS, normalizeMenuSettings } from './storefrontMenu';
import { DEFAULT_KARGONOMI_SETTINGS, normalizeKargonomiSettings } from './storefrontKargonomi';
import { DEFAULT_GOOGLE_MERCHANT_SETTINGS, normalizeGoogleMerchantSettings } from './storefrontGoogleMerchant';
import { normalizePublicSiteUrl } from './storefrontPublicUrl';
import {
  DEFAULT_ENABLED_LANGUAGES,
  DEFAULT_STOREFRONT_LANGUAGE,
  normalizeDefaultLanguage,
  normalizeEnabledLanguages
} from './storefrontLanguages';
import { buildCustomThemeCssVars, normalizeHexColor } from './storefrontColorUtils';
import { DEFAULT_GOOGLE_RATING, normalizeGoogleRating } from './storefrontGoogleRating';

export const STOREFRONT_THEME_IDS = ['blue', 'red', 'orange', 'green', 'black', 'custom'];

export const STOREFRONT_THEME_META = {
  blue: { labelKey: 'company.settings.themeBlue', color: '#2563eb' },
  red: { labelKey: 'company.settings.themeRed', color: '#dc2626' },
  orange: { labelKey: 'company.settings.themeOrange', color: '#ea580c' },
  green: { labelKey: 'company.settings.themeGreen', color: '#16a34a' },
  black: { labelKey: 'company.settings.themeBlack', color: '#171717' },
  custom: { labelKey: 'company.settings.themeCustom', color: '#2563eb', isCustom: true }
};

export const DEFAULT_CUSTOM_COLORS = {
  primary: '#2563eb'
};

export const STOREFRONT_THEME_CSS_VARS = {
  blue: {
    '--sf-50': '#eff6ff',
    '--sf-100': '#dbeafe',
    '--sf-500': '#3b82f6',
    '--sf-600': '#2563eb',
    '--sf-700': '#1d4ed8',
    '--sf-800': '#1e40af'
  },
  red: {
    '--sf-50': '#fef2f2',
    '--sf-100': '#fee2e2',
    '--sf-500': '#ef4444',
    '--sf-600': '#dc2626',
    '--sf-700': '#b91c1c',
    '--sf-800': '#991b1b'
  },
  orange: {
    '--sf-50': '#fff7ed',
    '--sf-100': '#ffedd5',
    '--sf-500': '#f97316',
    '--sf-600': '#ea580c',
    '--sf-700': '#c2410c',
    '--sf-800': '#9a3412'
  },
  green: {
    '--sf-50': '#f0fdf4',
    '--sf-100': '#dcfce7',
    '--sf-500': '#22c55e',
    '--sf-600': '#16a34a',
    '--sf-700': '#15803d',
    '--sf-800': '#166534'
  },
  black: {
    '--sf-50': '#fafafa',
    '--sf-100': '#f5f5f5',
    '--sf-500': '#525252',
    '--sf-600': '#171717',
    '--sf-700': '#0a0a0a',
    '--sf-800': '#000000'
  }
};

export const getThemeCssVars = (themeId, customColors = {}) => {
  if (themeId === 'custom') {
    return buildCustomThemeCssVars(customColors?.primary || DEFAULT_CUSTOM_COLORS.primary);
  }
  return STOREFRONT_THEME_CSS_VARS[STOREFRONT_THEME_IDS.includes(themeId) ? themeId : 'blue'];
};

export const normalizeCustomColors = (colors = {}) => ({
  primary: normalizeHexColor(colors.primary, DEFAULT_CUSTOM_COLORS.primary)
});

export const DEFAULT_STOREFRONT_SETTINGS = {
  liveSupportEnabled: false,
  whatsappEnabled: true,
  whatsappPhone: '905393583812',
  whatsappShowNumber: false,
  theme: 'blue',
  customColors: DEFAULT_CUSTOM_COLORS,
  favicon: '',
  publicSiteUrl: '',
  maintenanceMode: false,
  maintenanceTitle: '',
  maintenanceMessage: '',
  enabledLanguages: DEFAULT_ENABLED_LANGUAGES,
  defaultLanguage: DEFAULT_STOREFRONT_LANGUAGE,
  uiTranslations: {},
  reviewModerationEnabled: false,
  delivery: DEFAULT_DELIVERY_SETTINGS,
  payment: DEFAULT_PAYMENT_SETTINGS,
  kargonomi: DEFAULT_KARGONOMI_SETTINGS,
  googleMerchant: DEFAULT_GOOGLE_MERCHANT_SETTINGS,
  googleRating: DEFAULT_GOOGLE_RATING,
  menus: DEFAULT_MENU_SETTINGS,
  socialMedia: DEFAULT_SOCIAL_MEDIA,
  contentBlocks: DEFAULT_CONTENT_BLOCKS
};

export const normalizeStorefrontSettings = (settings = {}) => ({
  liveSupportEnabled: settings.liveSupportEnabled === true,
  whatsappEnabled: settings.whatsappEnabled !== false,
  whatsappPhone: String(settings.whatsappPhone || DEFAULT_STOREFRONT_SETTINGS.whatsappPhone).replace(/\D/g, '')
    || DEFAULT_STOREFRONT_SETTINGS.whatsappPhone,
  whatsappShowNumber: settings.whatsappShowNumber === true,
  theme: STOREFRONT_THEME_IDS.includes(settings.theme) ? settings.theme : 'blue',
  customColors: normalizeCustomColors(settings.customColors),
  favicon: String(settings.favicon || '').trim(),
  publicSiteUrl: normalizePublicSiteUrl(settings.publicSiteUrl),
  maintenanceMode: settings.maintenanceMode === true,
  maintenanceTitle: String(settings.maintenanceTitle || '').trim(),
  maintenanceMessage: String(settings.maintenanceMessage || '').trim(),
  enabledLanguages: normalizeEnabledLanguages(settings.enabledLanguages),
  defaultLanguage: normalizeDefaultLanguage(settings.defaultLanguage, normalizeEnabledLanguages(settings.enabledLanguages)),
  uiTranslations: settings.uiTranslations && typeof settings.uiTranslations === 'object' ? settings.uiTranslations : {},
  reviewModerationEnabled: settings.reviewModerationEnabled === true,
  delivery: normalizeDeliverySettings(settings.delivery),
  payment: normalizePaymentSettings(settings.payment),
  kargonomi: normalizeKargonomiSettings(settings.kargonomi),
  googleMerchant: normalizeGoogleMerchantSettings(settings.googleMerchant),
  googleRating: normalizeGoogleRating(settings.googleRating),
  menus: normalizeMenuSettings(settings.menus),
  socialMedia: normalizeSocialMedia(settings.socialMedia),
  contentBlocks: normalizeContentBlocks(settings.contentBlocks)
});

export const formatWhatsAppDisplay = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('90')) {
    return `+90 (${digits.slice(2, 5)}) ${digits.slice(5, 8)} ${digits.slice(8, 10)} ${digits.slice(10)}`;
  }
  if (digits.length === 11 && digits.startsWith('0')) {
    return `+90 (${digits.slice(1, 4)}) ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9)}`;
  }
  return digits ? `+${digits}` : '';
};
